import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, MenuItem, Select, FormControl,
  InputLabel, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#059669';
const DARK_GREEN = '#065f46';
const ORANGE = '#f59e0b';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.75rem', textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${ORANGE} 0%, #ffa726 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)', py: 1.2, px: 0.8,
  whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = { py: 1, px: 0.8, fontSize: '0.78rem', textAlign: 'center' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

// ─── Detail table (production rows) ──────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  
  const totalPCS = rows.reduce((sum, r) => sum + (parseFloat(r.PCS) || 0), 0);
  const totalCBM = rows.reduce((sum, r) => sum + (parseFloat(r.CBM) || 0), 0);
  
  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {['SR. NO.', 'QUALITY', 'LENGTH', 'WIDTH', 'THICKNESS', 'QTY (PCS)', 'CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f0fdf4',
              '&:hover': { backgroundColor: '#d1fae5' },
            }}>
              <TableCell sx={CELL_SX}>{i + 1}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, textAlign: 'left', pl: 1.5 }}>{row.QUALITY || '—'}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.LENGTH, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.WIDTH, 3)}</TableCell>
              <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.PCS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: DARK_GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
            </TableRow>
          ))}
          {/* Subtotal row */}
          <TableRow sx={{ backgroundColor: '#d1fae5', fontWeight: 700 }}>
            <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: DARK_GREEN }} colSpan={5}>TOTAL</TableCell>
            <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: DARK_GREEN }}>{fmtNum(totalPCS, 0)}</TableCell>
            <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: DARK_GREEN }}>{fmtNum(totalCBM, 3)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary table ────────────────────────────────────────────────────────────
function SummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  const hasShift = rows.some(r => r.SHIFT);
  const headers = hasShift ? ['COMPANY', 'SECTION', 'SHIFT', 'PCS', 'CBM'] : ['COMPANY', 'SECTION', 'PCS', 'CBM'];
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            {headers.map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const overall = String(row.COMPANY || '').trim().toUpperCase() === 'OVERALL';
            return (
              <TableRow key={i} sx={{
                backgroundColor: overall ? '#1b4332' : (i % 2 === 0 ? '#fff' : '#f0fdf4'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: overall ? '#fff' : DARK_GREEN, textAlign: 'left', pl: 1.5 }}>
                  {row.COMPANY || '—'}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, color: overall ? '#a7f3d0' : '#374151' }}>
                  {row.SECTION || '—'}
                </TableCell>
                {hasShift && (
                  <TableCell sx={{ ...CELL_SX, color: overall ? '#a7f3d0' : '#374151', fontWeight: 600 }}>
                    {row.SHIFT || '—'}
                  </TableCell>
                )}
                <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: overall ? '#a7f3d0' : GREEN }}>
                  {fmtNum(row.PCS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: overall ? '#a7f3d0' : DARK_GREEN }}>
                  {fmtNum(row.CBM, 3)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// For DAY/NIGHT: 3 recordsets
// For BOTH: 5 recordsets (prepDay, prepNight, grDay, grNight, summary)
const TAB_LABELS_SINGLE = ['REFACE DETAIL', 'GENERAL REFACE DETAIL', 'SUMMARY'];
const TAB_LABELS_BOTH   = ['REFACE - DAY', 'REFACE - NIGHT', 'GENERAL REFACE - DAY', 'GENERAL REFACE - NIGHT', 'SUMMARY'];

const DailyPrepressReportView = ({ onBackClick, user }) => {
  const [date, setDate]       = useState(null);
  const [shift, setShift]     = useState('DAY');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);
  const [tab, setTab]         = useState(0);

  const isBoth = shift === 'BOTH';
  const tabLabels = isBoth ? TAB_LABELS_BOTH : TAB_LABELS_SINGLE;

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/prepressreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date and shift.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    
    if (isBoth) {
      const sheets = [
        { label: 'Reface Day',          rows: data[0] },
        { label: 'Reface Night',        rows: data[1] },
        { label: 'General Reface Day',    rows: data[2] },
        { label: 'General Reface Night',  rows: data[3] },
        { label: 'Summary',               rows: data[4] },
      ];
      sheets.forEach(({ label, rows }) => {
        if (rows?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
      });
    } else {
      const sheets = [
        { label: 'Reface Detail',       rows: data[0] },
        { label: 'General Reface Detail', rows: data[1] },
        { label: 'Summary',               rows: data[2] },
      ];
      sheets.forEach(({ label, rows }) => {
        if (rows?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
      });
    }
    
    XLSX.writeFile(wb, `Prepress_${shift}_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(5,150,105,0.1)', border: '1px solid #d1fae5' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, ${DARK_GREEN} 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <PrecisionManufacturingIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: DARK_GREEN }}>Daily Reface Production Summary</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Prepress production by date and shift</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                  <MenuItem value="BOTH">BOTH (DAY + NIGHT)</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{ minWidth: 140, backgroundColor: GREEN, '&:hover': { backgroundColor: DARK_GREEN }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #d1fae5' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)} textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{ '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' }, '& .Mui-selected': { color: GREEN }, '& .MuiTabs-indicator': { backgroundColor: GREEN } }}
            >
              {tabLabels.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}>
              Excel
            </Button>
          </Box>

          {isBoth ? (
            <>
              {tab === 0 && <DetailTable  rows={data[0]} />}
              {tab === 1 && <DetailTable  rows={data[1]} />}
              {tab === 2 && <DetailTable  rows={data[2]} />}
              {tab === 3 && <DetailTable  rows={data[3]} />}
              {tab === 4 && <SummaryTable rows={data[4]} />}
            </>
          ) : (
            <>
              {tab === 0 && <DetailTable  rows={data[0]} />}
              {tab === 1 && <DetailTable  rows={data[1]} />}
              {tab === 2 && <SummaryTable rows={data[2]} />}
            </>
          )}
        </Card>
      )}
      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label={`Send Prepress ${shift} Report to WhatsApp`}
            apiUrl={date ? `${API_URL}/api/prepress/send?date=${date.format('YYYY-MM-DD')}&shift=${shift}` : null}
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default DailyPrepressReportView;
